import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaFacebook, FaTwitter, FaLinkedin, FaPaperPlane } from 'react-icons/fa';
import contactImage from '../assets/Contact2.jpg';
import './Contact.css';

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setSubmitted(true);
    setIsLoading(false);
    setTimeout(() => setSubmitted(false), 5000);
    setForm({ name: '', email: '', message: '' });
  };

  return (
    <div className="contact-container">
      {/* Hero Section */}
      <motion.div
        className="contact-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        style={{ backgroundImage: `url(${contactImage})` }}
      >
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <motion.h1
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Be a Part of Our GSK Family
          </motion.h1>
          <motion.p
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Join us in our mission to shape bright futures and responsible citizens.
          </motion.p>
          <motion.button
            className="contact-cta"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            onClick={() => document.querySelector('#contactForm').scrollIntoView({ behavior: 'smooth' })}
          >
            Get in Touch
          </motion.button>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="contact-main">
        {/* Contact Info Section */}
        <motion.div 
          className="contact-info-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeIn}
        >
          <div className="contact-info-card">
            <h2>Institute Information</h2>
            <p>Reach out to us through these channels:</p>
            
            <div className="info-items">
              <div className="info-item">
                <div className="info-icon">
                  <FaPhone />
                </div>
                <div className="info-text">
                  <h3>Phone</h3>
                  <p>+91 98765 43210</p>
                  <p>+91 12345 67890</p>
                </div>
              </div>
              
              <div className="info-item">
                <div className="info-icon">
                  <FaEnvelope />
                </div>
                <div className="info-text">
                  <h3>Email</h3>
                  <p>info@ithunt.edu.in</p>
                  <p>admissions@ithunt.edu.in</p>
                </div>
              </div>
              
              <div className="info-item">
                <div className="info-icon">
                  <FaMapMarkerAlt />
                </div>
                <div className="info-text">
                  <h3>Address</h3>
                  <p>GSK College</p>
                  <p>Fatuha Road,Hanumanganj,Uttar Pradesh 221505</p>
                </div>
              </div>
            </div>
            
            <div className="social-section">
              <h3>Follow Us</h3>
              <div className="social-links">
                <a href="#" className="social-link"><FaFacebook /></a>
                <a href="#" className="social-link"><FaTwitter /></a>
                <a href="#" className="social-link"><FaLinkedin /></a>
              </div>
            </div>
          </div>
          
          <div className="contact-image">
            <img src={contactImage} alt="GSK College Campus" />
          </div>
        </motion.div>

        {/* Contact Form Section */}
        <motion.div 
          className="contact-form-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeIn}
          transition={{ delay: 0.2 }}
        >
          <div className="form-container">
            <h2>Admission Enquiry</h2>
            
            <form className="contact-form" onSubmit={handleSubmit} id="contactForm">
              <div className="form-group">
                <label htmlFor="name">Your Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Enter your name"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Your Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Enter your email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Your Message</label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Course or admission queries..."
                  rows="5"
                  value={form.message}
                  onChange={handleChange}
                  required
                ></textarea>
              </div>
              
              <motion.button
                type="submit"
                className="submit-button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="button-loader"></span>
                ) : (
                  <>
                    <FaPaperPlane className="button-icon" />
                    Send Enquiry
                  </>
                )}
              </motion.button>
              
              {submitted && (
                <motion.div
                  className="success-message"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="success-checkmark">✓</div>
                  <div>
                    <h3>Thank You!</h3>
                    <p>Your enquiry has been submitted successfully.</p>
                  </div>
                </motion.div>
              )}
            </form>
          </div>
        </motion.div>
      </div>

      {/* Map Section */}
      <motion.div 
        className="map-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        transition={{ delay: 0.4 }}
      >
        <h2>Our Location</h2>
        <p>Visit our campus in Hanumanganj Fatuha Road,Prayagraj</p>
        
        <div className="map-container">
              <iframe 
            src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d900.898371823198!2d82.03240396946589!3d25.41841459859811!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sin!4v1754129858738!5m2!1sen!2sin" 
            width="100%" 
            height="450" 
            allowfullscreen="" 
            loading="lazy" 
            referrerpolicy="no-referrer-when-downgrade">
            </iframe>

        </div>
      </motion.div>
    </div>
  );
};

export default Contact;